/**
 * @fileoverview Textarea widget — multi-line text.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render <textarea> with rows, placeholder, minLength, maxLength.
 *
 * @component TextareaField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';

export function TextareaField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { placeholder?: string; rows?: number; minLength?: number; maxLength?: number };
  const v = value ?? '';
  const exceedsMax = extras.maxLength != null && v.length > extras.maxLength;
  const belowMin = extras.minLength != null && v.length > 0 && v.length < extras.minLength;
  const computedError = errorMessage
    ?? (exceedsMax ? `Max length ${extras.maxLength}` : undefined)
    ?? (belowMin ? `Min length ${extras.minLength}` : undefined);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="textarea"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet}
    >
      <textarea
        id={field.id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={extras.placeholder}
        rows={extras.rows ?? 3}
        minLength={extras.minLength}
        maxLength={extras.maxLength}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={!!computedError}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm outline-hidden focus:ring-2 focus:ring-blue-300 resize-none disabled:opacity-50"
      />
      {extras.maxLength && (
        <p className="text-[10px] text-gray-400 mt-0.5">{(value ?? '').length} / {extras.maxLength}</p>
      )}
    </FieldWrapper>
  );
}
